import { LineChartAxisSide, type LineChartAxis, type LineChartAxisLabel } from '@shigo-ui/viz';

import { DashboardMetric } from './model';

export enum DashboardChartUnit {
  Count = 'COUNT',
  Percentage = 'PERCENTAGE',
  Currency = 'CURRENCY',
}

export interface DashboardChartMetricAxisInput {
  metric: string;
  unit: DashboardChartUnit;
  separateAxis?: boolean;
  label: string;
  color: string;
  values: number[];
  valueFormatter: (value: number) => string;
}

interface DashboardChartAxisLayout {
  axes: LineChartAxis[];
  axisIdByMetric: Map<string, string>;
}

interface AxisGroup {
  unit: DashboardChartUnit;
  separateAxis: boolean;
  inputs: DashboardChartMetricAxisInput[];
  minMagnitude: number;
  maxMagnitude: number;
}

// Share axes for comparable metrics while keeping values over 3x apart visually distinct.
const compatibleDomainRatio = 3;

export function buildDashboardChartAxisLayout(
  inputs: DashboardChartMetricAxisInput[],
): DashboardChartAxisLayout {
  const groups: AxisGroup[] = [];

  inputs.forEach((input) => {
    const magnitude = metricMagnitude(input.values);
    const unit = input.unit;
    const group = groups.find(
      (candidate) =>
        !input.separateAxis &&
        !candidate.separateAxis &&
        candidate.unit === unit &&
        domainsAreCompatible(candidate, magnitude),
    );

    if (group) {
      group.inputs.push(input);
      extendDomain(group, magnitude);
      return;
    }

    groups.push({
      unit,
      separateAxis: input.separateAxis ?? false,
      inputs: [input],
      minMagnitude: magnitude,
      maxMagnitude: magnitude,
    });
  });

  const axisIdByMetric = new Map<string, string>();
  const axes = groups.map<LineChartAxis>((group, index) => {
    const id = `dashboard-${group.unit.toLowerCase()}-${String(index)}`;
    group.inputs.forEach((input) => axisIdByMetric.set(input.metric, id));
    const labels: LineChartAxisLabel[] = group.inputs.map(({ label, color }) => ({
      label,
      color,
    }));
    const first = group.inputs[0];
    if (!first) throw new Error('Dashboard chart axis group cannot be empty.');
    return {
      id,
      label: labels.map(({ label }) => label).join(' & '),
      labels,
      color: labels.length === 1 ? first.color : 'var(--muted-foreground)',
      side: index % 2 === 0 ? LineChartAxisSide.Left : LineChartAxisSide.Right,
      valueFormatter: first.valueFormatter,
    };
  });

  return { axes, axisIdByMetric };
}

export function dashboardMetricUnit(metric: DashboardMetric) {
  switch (metric) {
    case DashboardMetric.AdClicks:
    case DashboardMetric.AdRequests:
    case DashboardMetric.AdImpressions:
      return DashboardChartUnit.Count;
    case DashboardMetric.FillRate:
      return DashboardChartUnit.Percentage;
    case DashboardMetric.Rpm:
    case DashboardMetric.Srpm:
    case DashboardMetric.Revenue:
    case DashboardMetric.PublisherRevenue:
    case DashboardMetric.Profit:
    case DashboardMetric.NetworkRevenue:
    case DashboardMetric.SuperAdminProfit:
      return DashboardChartUnit.Currency;
  }
}

function metricMagnitude(values: number[]) {
  return values.reduce((largest, value) => {
    if (!Number.isFinite(value)) return largest;
    return Math.max(largest, Math.abs(value));
  }, 0);
}

function domainsAreCompatible(group: AxisGroup, magnitude: number) {
  if (magnitude === 0 || group.maxMagnitude === 0) return true;
  const prospectiveMin = Math.min(group.minMagnitude || magnitude, magnitude);
  const prospectiveMax = Math.max(group.maxMagnitude, magnitude);
  return prospectiveMax / prospectiveMin <= compatibleDomainRatio;
}

function extendDomain(group: AxisGroup, magnitude: number) {
  if (magnitude === 0) return;
  group.minMagnitude =
    group.minMagnitude === 0 ? magnitude : Math.min(group.minMagnitude, magnitude);
  group.maxMagnitude = Math.max(group.maxMagnitude, magnitude);
}
